import { useState, useEffect } from 'react';
import { useQuery } from 'data-fetching-client';
import getIn from 'immutable-less/transmute/getIn';
import { createFieldDataAddonProperty } from 'automation-ui-action-features/actions/static-branch/staticBranchUtils';
import { OBJECT_PROPERTY, FIELD_DATA, FETCHED_OBJECT_PROPERTY, ENROLLMENT_EVENT_PROPERTY, ENROLLMENT_DATASET_COLUMN } from 'automation-ui-formula-editor/formula-editor/data-passing/constants/DataSourceOptions';
import { convertToAddonPropertyOption } from 'automation-ui-action-features/object-type-properties/propertyUtils';
import { useGetDataProperty } from 'automation-ui-formula-editor/data-sources/hooks/useGetDataProperty';
import { GET_CRM_PROPERTY_DEFINITION } from 'automation-ui-formula-editor/data-sources/queries/getCRMPropertyDefinition';
import getTokenParts from 'automation-ui-formula-editor/data-sources/utils/getTokenParts';
import { APPEND_OBJECT_PROPERTY } from 'automation-ui-flow-core/fields/PropertyValueTypes';
import { ENROLLED_OBJECT } from 'automation-ui-formula-editor/data-sources/constants/dataSourcesConstants';
import { useDataSourcesContext } from 'automation-ui-formula-editor/data-sources/DataSourcesContext';
import { getToObjectType } from 'automation-ui-formula-editor/data-passing-panel/utils/getToObjectType';
function usePropertyAsAddon({
  stagedAction,
  propertyDefinitionsAsAddon,
  propertyDefinitionsAsAddonLoading,
  getActionOutputsForTargetActionById,
  previousActionId
}) {
  const [property, setProperty] = useState(undefined);
  const [propertyAsAddon, setPropertyAsAddon] = useState();
  const [isPropertyAsAddonLoading, setIsPropertyAsAddonLoading] = useState(true);
  const metadata = stagedAction.getMetadata();
  let prefix = '';
  const inputType = metadata.getInputType();
  let propertyName = metadata.getInputValue().getPropertyName();
  if (inputType === OBJECT_PROPERTY) {
    prefix = ENROLLED_OBJECT;
  } else if (inputType === FETCHED_OBJECT_PROPERTY || inputType === ENROLLMENT_EVENT_PROPERTY || inputType === ENROLLMENT_DATASET_COLUMN) {
    const dataToken = metadata.getInputValue().getPropertyToken();
    if (dataToken) {
      const dataTokenParts = getTokenParts(dataToken);
      prefix = dataTokenParts.prefix;
      propertyName = dataTokenParts.property;
    }
  }
  const {
    flowObjectTypeId
  } = useDataSourcesContext();
  const {
    data,
    error,
    loading: dataTokenLoading
  } = useGetDataProperty({
    prefix,
    property: propertyName
  });
  let toObjectTypeId;
  if (inputType === ENROLLMENT_EVENT_PROPERTY) {
    toObjectTypeId = getIn(['dataSourceDefinition', 'metadata', 'eventTypeId'], data);
  } else if (inputType === OBJECT_PROPERTY || inputType === APPEND_OBJECT_PROPERTY || inputType === ENROLLMENT_DATASET_COLUMN) {
    toObjectTypeId = getIn(['dataSourceDefinition', 'metadata', 'objectTypeId'], data);
  } else if (data) {
    toObjectTypeId = getToObjectType(data.dataSourceDefinition, flowObjectTypeId);
  }
  const {
    data: {
      getCRMPropertyDefinition: propertyData = undefined
    } = {},
    loading: propertyDefinitionLoading
  } = useQuery(GET_CRM_PROPERTY_DEFINITION, {
    skip: !prefix || !propertyName || !toObjectTypeId,
    variables: {
      objectType: toObjectTypeId,
      property: propertyName
    }
  });
  useEffect(() => {
    setIsPropertyAsAddonLoading(dataTokenLoading && propertyDefinitionLoading);
    if (inputType === OBJECT_PROPERTY && !propertyDefinitionsAsAddonLoading) {
      const newPropertyAsAddon = propertyDefinitionsAsAddon.find(prop => prop.value === propertyName);
      setProperty(propertyData && propertyData.property);
      setPropertyAsAddon(newPropertyAsAddon);
    } else if (inputType === FIELD_DATA) {
      const newPropertyAsAddon = createFieldDataAddonProperty(getActionOutputs({
        actionSpec: stagedAction
      }), metadata.getInputValue());
      setProperty(propertyData && propertyData.property);
      setPropertyAsAddon(newPropertyAsAddon);
    } else if (inputType === FETCHED_OBJECT_PROPERTY) {
      if (dataTokenLoading) {
        return;
      }
      if (error) {
        // we couldn't resolve the data token
        return;
      }
      if (data && getIn(['dataSourceProperty', 'name'], data) === propertyName && propertyData) {
        setProperty(propertyData && propertyData.property);
        setPropertyAsAddon(convertToAddonPropertyOption({
          property: propertyData.property,
          objectTypeId: toObjectTypeId
        }));
      }
    } else if (inputType === ENROLLMENT_DATASET_COLUMN) {
      if (dataTokenLoading) {
        return;
      }
      if (error) {
        // we couldn't resolve the data token
        return;
      }
      if (data && getIn(['dataSourceProperty', 'name'], data) === propertyName) {
        const typeDefinition = getIn(['dataSourceProperty', 'metadata', 'typeDefinition'], data);
        const propertyFromDataToken = getPropertyFromDataToken(typeDefinition);
        setProperty(propertyFromDataToken);
        setPropertyAsAddon(convertToAddonPropertyOption({
          property: propertyFromDataToken,
          objectTypeId: toObjectTypeId
        }));
      }
    } else if (inputType === ENROLLMENT_EVENT_PROPERTY) {
      if (dataTokenLoading) {
        return;
      }
      if (error) {
        // we couldn't resolve the data token
        return;
      }
      if (data && getIn(['dataSourceProperty', 'name'], data) === propertyName && propertyData) {
        const typeDefinition = getIn(['dataSourceProperty', 'metadata', 'typeDefinition'], data);
        const propertyFromDataToken = getPropertyFromDataToken(typeDefinition);

        /* trust framework app settings' property info as source of truth over the original properties for a variety of improvements
        ex: may need to make labels more user-friendly, may need to provide resolvers even though property is a number on the BE
        sourced from https://tools.hubteam.com/puma/framework-app-settings/automationEventTriggers/entries */
        const combinedProperty = Object.assign({}, propertyData.property, propertyFromDataToken);
        setProperty(combinedProperty);
        setPropertyAsAddon(convertToAddonPropertyOption({
          property: combinedProperty,
          objectTypeId: toObjectTypeId
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyDefinitionsAsAddon, data, stagedAction, propertyData, error, dataTokenLoading, propertyDefinitionLoading]);

  // DEV NOTE: If added to the useEffect in this hook, function
  // will need to be wrapped in a useCallback to avoid render loops
  // and odd behavior.
  function getActionOutputs({
    actionSpec
  }) {
    const actionId = actionSpec.getActionId();
    const targetActionId = actionId || previousActionId;
    return targetActionId ? getActionOutputsForTargetActionById(targetActionId, actionSpec.isNew()) : [];
  }
  function getPropertyFromDataToken(typeDefinition) {
    let propertyFromDataToken = {};
    if (typeDefinition) {
      const referencedObjectType = typeDefinition.referencedObjectType || typeDefinition.externalOptionsReferenceType;
      propertyFromDataToken = {
        label: typeDefinition.label || '',
        externalOptions: typeDefinition.externalOptions,
        referencedObjectType,
        externalOptionsReferenceType: referencedObjectType,
        /* handles case where property isn't an enum on the BE, but we need to display enums for UX reasons
            ex: email names are saved as  numerical ids on the BE, but we want to display options */
        fieldType: referencedObjectType ? 'enumeration' : typeDefinition.type,
        type: typeDefinition.type
      };
    }
    return propertyFromDataToken;
  }
  return {
    property,
    propertyAsAddon,
    isPropertyAsAddonLoading
  };
}
export default usePropertyAsAddon;